import { useState, useRef, useCallback, useEffect } from 'react';
import { generateTTSAudio } from '../../../config/api';

export function useTTS() {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const generationRef = useRef(0);
  const pendingResolveRef = useRef<(() => void) | null>(null);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current = null;
      }
    };
  }, []);

  const stop = useCallback(() => {
    generationRef.current++;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (pendingResolveRef.current) {
      pendingResolveRef.current();
      pendingResolveRef.current = null;
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  const playText = useCallback(
    async (text: string): Promise<void> => {
      // Stop any currently playing audio
      stop();
      const generation = generationRef.current;

      try {
        setIsLoading(true);
        setError(false);
        const audioUrl = await generateTTSAudio(text);

        // Bail out if stop() or another playText() was called while fetching
        if (generationRef.current !== generation) return;

        setIsLoading(false);

        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        setIsPlaying(true);

        return new Promise<void>((resolve) => {
          pendingResolveRef.current = resolve;
          audio.onended = () => {
            pendingResolveRef.current = null;
            setIsPlaying(false);
            audioRef.current = null;
            resolve();
          };
          audio.onerror = (e) => {
            pendingResolveRef.current = null;
            setIsPlaying(false);
            audioRef.current = null;
            console.error('Audio playback error:', e);
            // Resolve instead of reject so the flow continues even if audio fails
            resolve();
          };
          audio.play().catch((err) => {
            pendingResolveRef.current = null;
            setIsPlaying(false);
            audioRef.current = null;
            console.error('Audio play() error:', err);
            // Resolve so flow continues
            resolve();
          });
        });
      } catch (err) {
        console.error('TTS generation error:', err);
        setIsLoading(false);
        setIsPlaying(false);
        setError(true);
        // Don't throw — allow flow to continue without audio
      }
    },
    [stop]
  );

  return { playText, isLoading, isPlaying, error, stop };
}
